import {
  type MappingSystem,
  type MappedId,
  type MappingEntity,
  type MappingRef,
  type TenantMapping,
} from "./types";

const ids = (e: MappingEntity, system: MappingSystem) =>
  e.integrations
    .filter((i) => i.integrator?.toLowerCase() === system.toLowerCase())
    .map((i) => i.integratorId);

const isNumeric = (s: string) => /^\d+$/.test(s);

const highest = (values: string[]) =>
  values.reduce((a, b) =>
    (isNumeric(a) && isNumeric(b) ? Number(b) > Number(a) : b > a) ? b : a,
  );

export const isSameSystem = (a: MappingSystem, b: MappingSystem) =>
  a.toLowerCase() === b.toLowerCase();

export function findMapping<
  T extends string | number,
  F extends MappingSystem,
  S extends MappingSystem,
>(tenants: TenantMapping[], from: MappingRef<T, F>, to: S): MappedId<T, F, S> {
  // Same system is a no-op: an entity can hold several ids in one system, and
  // picking the highest would silently swap the caller's id for a sibling.
  if (isSameSystem(from.system, to)) return from.id as MappedId<T, F, S>;
  const id = String(from.id);
  for (const t of tenants) {
    const entities: MappingEntity[] =
      from.type === "tenant"
        ? [t.tenant]
        : from.type === "site"
          ? t.sites
          : t.sites.flatMap((s) => s.cages);
    const hit = entities.find((e) => ids(e, from.system).includes(id));
    const targets = hit ? ids(hit, to) : [];
    if (targets.length === 0) continue;
    return highest(targets) as MappedId<T, F, S>;
  }
  return undefined as MappedId<T, F, S>;
}
